/**
 * AnnotationBridgesBuilder.ts - renders concrete bridges from compiled masks.
 * Bridge feature pixels become concrete bridge geometry.
 */

import { createBox, mergeMeshes } from "../assets/ProceduralAssets";
import type { MeshAsset } from "../assets/MeshAsset";
import {
  buildTerrainSurfaceMask,
  sampleTerrainHeight,
  type TerrainSurface,
  type TerrainAsset,
} from "../assets/TerrainAsset";
import type { Mat4, StaticMeshPass } from "../passes/StaticMeshPass";
import type { Aabb } from "./Collisions";
import { connectedComponents8 } from "./building-extrude";

export interface BridgeFootprint {
  x: number;
  z: number;
  length: number;
  width: number;
  roadWidth: number;
  yaw: number;
  pixelCount: number;
  deckBaseY?: number;
  deckBaseMinusY?: number;
  deckBasePlusY?: number;
  terrainMinusY?: number;
  terrainPlusY?: number;
}

interface BridgeBoxPart {
  id: string;
  centerX: number;
  centerY: number;
  centerZ: number;
  sizeX: number;
  sizeY: number;
  sizeZ: number;
  color: [number, number, number];
  collides: boolean;
  slopeYPerX?: number;
}

const GROUP_PREFIX = "annotation.bridge";
const MIN_COMPONENT_PIXELS = 4;
const DEFAULT_ROAD_WIDTH_M = 5.0;
const MIN_ROAD_WIDTH_M = 2.8;
const DUPLICATE_CENTER_TOLERANCE_M = 3.0;
const DUPLICATE_LATERAL_TOLERANCE_M = 8.0;
const DUPLICATE_YAW_TOLERANCE_RAD = 0.35;
const BANK_LANDING_M = 0.45;
const ROAD_CONTEXT_PAD_M = 6.0;
const ROAD_SURFACE_SIDE_ALLOWANCE_M = 1.15;
const ABUTMENT_THICKNESS_M = 1.15;
const END_BEARING_M = 0.15;
const SIDE_SHOULDER_WIDTH_M = 0.28;
const SIDE_PARAPET_ALLOWANCE_M = 0.22;
const SIDE_OVERHANG_M = 0.0;
const FALLBACK_DECK_BASE_Y = 1.0;
const DECK_THICKNESS_M = 0.38;
const SURFACE_THICKNESS_M = 0.035;
const ROAD_SURFACE_TERRAIN_MARGIN_M = 0.02;
const WALL_BOTTOM_M = -0.55;
const PARAPET_HEIGHT_M = 1.0;
const PARAPET_WIDTH_M = SIDE_PARAPET_ALLOWANCE_M;

const CONCRETE_FACE: [number, number, number] = [0.62, 0.64, 0.61];
const CONCRETE_TOP: [number, number, number] = [0.73, 0.74, 0.7];
const SHOULDER_TOP: [number, number, number] = [0.7, 0.71, 0.68];
const ROAD_TOP: [number, number, number] = [0.66, 0.67, 0.63];

export class AnnotationBridgesBuilder {
  constructor(private readonly staticMeshPass: StaticMeshPass) {}

  build(
    terrain: TerrainAsset,
    waterLevel: number,
    staticAabbs: Aabb[],
  ): BridgeFootprint[] {
    const bridges = extractBridgesFromAnnotation(terrain);
    if (bridges.length === 0) return bridges;

    let bridgeIndex = 0;
    for (const bridge of bridges) {
      bridgeIndex++;
      const meshId = `${GROUP_PREFIX}.${bridgeIndex}`;
      this.staticMeshPass.addGroup(
        meshId,
        createBridgeMesh(bridge, meshId),
        0,
        {
          castsShadow: true,
          castsHardShadow: true,
          receivesShadow: true,
        },
      );
      this.staticMeshPass.addInstance(
        meshId,
        bridgeInstanceMatrix(bridge, waterLevel),
      );
      staticAabbs.push(...bridgeAabbs(bridge, waterLevel));
    }

    console.log(
      `[Bridges] rendered ${bridges.length} concrete bridge(s) from compiled feature masks`,
    );
    return bridges;
  }
}

export function extractBridgesFromAnnotation(
  terrain: TerrainAsset,
): BridgeFootprint[] {
  const bridgeMask = buildTerrainSurfaceMask(terrain, "bridge");
  if (!bridgeMask) return [];

  const bridges = connectedComponents8(
    bridgeMask.mask,
    bridgeMask.width,
    bridgeMask.height,
  )
    .filter((component) => component.length >= MIN_COMPONENT_PIXELS)
    .map((component) =>
      bridgeFootprintFromComponent(
        terrain,
        bridgeMask.width,
        bridgeMask.height,
        component,
      ),
    )
    .filter((bridge): bridge is BridgeFootprint => bridge !== null);
  return mergeDuplicateBridgeFootprints(bridges);
}

function bridgeFootprintFromComponent(
  terrain: TerrainAsset,
  maskWidth: number,
  maskHeight: number,
  component: Array<[number, number]>,
): BridgeFootprint | null {
  const worldWidth = terrain.worldWidthM;
  const worldHeight = terrain.worldHeightM;
  const halfWorldWidth = worldWidth * 0.5;
  const halfWorldHeight = worldHeight * 0.5;
  const pxToWorldX = worldWidth / maskWidth;
  const pxToWorldZ = worldHeight / maskHeight;
  const cellM = Math.max(pxToWorldX, pxToWorldZ);

  const worldPoints: Array<[number, number]> = component.map(([col, row]) => [
    -halfWorldWidth + (col + 0.5) * pxToWorldX,
    -halfWorldHeight + (row + 0.5) * pxToWorldZ,
  ]);

  const bridgePca = pcaAxis(worldPoints);
  if (!bridgePca) return null;

  const roadWidthSearchPadPx = Math.max(2, Math.ceil(1.5 / cellM));
  const roadLandingSearchPadPx = Math.max(
    roadWidthSearchPadPx,
    Math.ceil(ROAD_CONTEXT_PAD_M / cellM),
  );
  const roadWidthPoints = localAnnotationSurfacePointsNearComponent(
    terrain,
    maskWidth,
    maskHeight,
    component,
    "road",
    roadWidthSearchPadPx,
  );
  const roadContextPoints = localAnnotationSurfacePointsNearComponent(
    terrain,
    maskWidth,
    maskHeight,
    component,
    "road",
    roadLandingSearchPadPx,
  );
  const roadPca = pcaAxis(roadWidthPoints);

  let spanAxisX: number;
  let spanAxisZ: number;
  if (roadPca) {
    spanAxisX = roadPca.axisX;
    spanAxisZ = roadPca.axisZ;
  } else {
    spanAxisX = bridgePca.axisX;
    spanAxisZ = bridgePca.axisZ;
  }
  const spanAxisLength = Math.hypot(spanAxisX, spanAxisZ);
  if (spanAxisLength < 1e-6) return null;
  spanAxisX /= spanAxisLength;
  spanAxisZ /= spanAxisLength;
  let widthAxisX = -spanAxisZ;
  let widthAxisZ = spanAxisX;

  let measurements = measureBridgeAxes(
    worldPoints,
    roadWidthPoints,
    spanAxisX,
    spanAxisZ,
    widthAxisX,
    widthAxisZ,
    cellM,
  );
  if (!measurements) return null;

  const roadCenterline = roadCenterlineAxis(
    roadWidthPoints,
    spanAxisX,
    spanAxisZ,
    widthAxisX,
    widthAxisZ,
    measurements.widthMid,
    measurements.roadSurfaceWidth,
  );
  if (roadCenterline) {
    spanAxisX = roadCenterline.axisX;
    spanAxisZ = roadCenterline.axisZ;
    widthAxisX = -spanAxisZ;
    widthAxisZ = spanAxisX;
    measurements = measureBridgeAxes(
      worldPoints,
      roadWidthPoints,
      spanAxisX,
      spanAxisZ,
      widthAxisX,
      widthAxisZ,
      cellM,
    );
    if (!measurements) return null;
  }

  const { spanExtents, roadSurfaceWidth, bridgeWidth, widthMid } = measurements;
  const roadAxisPoints = roadPca
    ? roadContextPoints.filter((point) => {
        const widthCoord = point[0] * widthAxisX + point[1] * widthAxisZ;
        return Math.abs(widthCoord - widthMid) <= bridgeWidth * 0.75;
      })
    : [];
  const roadSpanExtents =
    roadAxisPoints.length >= 2
      ? projectedExtents(roadAxisPoints, spanAxisX, spanAxisZ)
      : null;

  const halfCellM = cellM * 0.5;
  const waterSpanMin = spanExtents.min - halfCellM;
  const waterSpanMax = spanExtents.max + halfCellM;
  const landingMinus = bridgeBankLanding(
    roadSpanExtents ? waterSpanMin - roadSpanExtents.min + halfCellM : null,
    cellM,
  );
  const landingPlus = bridgeBankLanding(
    roadSpanExtents ? roadSpanExtents.max - waterSpanMax + halfCellM : null,
    cellM,
  );
  const spanMin = waterSpanMin - landingMinus;
  const spanMax = waterSpanMax + landingPlus;
  const spanMid = (spanMin + spanMax) * 0.5;
  const footprintCenterX = spanAxisX * spanMid + widthAxisX * widthMid;
  const footprintCenterZ = spanAxisZ * spanMid + widthAxisZ * widthMid;
  const deckProfile = bridgeDeckProfileFromHeightmap(
    terrain,
    spanAxisX,
    spanAxisZ,
    widthAxisX,
    widthAxisZ,
    spanMin,
    spanMax,
    widthMid,
    roadSurfaceWidth,
  );

  return {
    x: footprintCenterX,
    z: footprintCenterZ,
    length: Math.max(1.5, spanMax - spanMin),
    width: bridgeWidth,
    roadWidth: roadSurfaceWidth,
    yaw: Math.atan2(spanAxisZ, spanAxisX),
    pixelCount: component.length,
    ...deckProfile,
  };
}

function measureBridgeAxes(
  bridgePoints: ReadonlyArray<readonly [number, number]>,
  roadPoints: ReadonlyArray<readonly [number, number]>,
  spanAxisX: number,
  spanAxisZ: number,
  widthAxisX: number,
  widthAxisZ: number,
  cellM: number,
): {
  spanExtents: { min: number; max: number };
  roadSurfaceWidth: number;
  bridgeWidth: number;
  widthMid: number;
} | null {
  const spanExtents = projectedExtents(bridgePoints, spanAxisX, spanAxisZ);
  const bridgeWidthExtents = projectedExtents(
    bridgePoints,
    widthAxisX,
    widthAxisZ,
  );
  const roadWidthExtents =
    roadPoints.length >= 2
      ? projectedExtents(roadPoints, widthAxisX, widthAxisZ)
      : null;
  const widthExtents = roadWidthExtents ?? bridgeWidthExtents;
  if (!spanExtents || !bridgeWidthExtents || !widthExtents) return null;

  const measuredRoadWidth = roadWidthExtents
    ? roadWidthExtents.max - roadWidthExtents.min + cellM
    : DEFAULT_ROAD_WIDTH_M;
  const roadSurfaceWidth = Math.max(
    MIN_ROAD_WIDTH_M,
    measuredRoadWidth + ROAD_SURFACE_SIDE_ALLOWANCE_M * 2,
  );
  return {
    spanExtents,
    roadSurfaceWidth,
    bridgeWidth:
      roadSurfaceWidth +
      SIDE_SHOULDER_WIDTH_M * 2 +
      SIDE_PARAPET_ALLOWANCE_M * 2,
    widthMid: (widthExtents.min + widthExtents.max) * 0.5,
  };
}

function roadCenterlineAxis(
  roadPoints: ReadonlyArray<readonly [number, number]>,
  spanAxisX: number,
  spanAxisZ: number,
  widthAxisX: number,
  widthAxisZ: number,
  widthMid: number,
  roadSurfaceWidth: number,
): { axisX: number; axisZ: number } | null {
  const halfLane = Math.max(MIN_ROAD_WIDTH_M, roadSurfaceWidth * 0.75);
  const lanePoints = roadPoints.filter((point) => {
    const widthCoord = point[0] * widthAxisX + point[1] * widthAxisZ;
    return Math.abs(widthCoord - widthMid) <= halfLane;
  });
  if (lanePoints.length < 8) return null;

  const spanExtents = projectedExtents(lanePoints, spanAxisX, spanAxisZ);
  if (!spanExtents) return null;
  const spanLength = spanExtents.max - spanExtents.min;
  if (spanLength < 2.0) return null;
  const endBand = Math.max(2.0, spanLength * 0.2);
  const minus = centroidOfProjectedBand(
    lanePoints,
    spanAxisX,
    spanAxisZ,
    spanExtents.min,
    spanExtents.min + endBand,
  );
  const plus = centroidOfProjectedBand(
    lanePoints,
    spanAxisX,
    spanAxisZ,
    spanExtents.max - endBand,
    spanExtents.max,
  );
  if (!minus || !plus) return null;

  let axisX = plus[0] - minus[0];
  let axisZ = plus[1] - minus[1];
  const axisLength = Math.hypot(axisX, axisZ);
  if (axisLength < 1e-6) return null;
  axisX /= axisLength;
  axisZ /= axisLength;
  if (axisX * spanAxisX + axisZ * spanAxisZ < 0) {
    axisX = -axisX;
    axisZ = -axisZ;
  }
  return { axisX, axisZ };
}

function centroidOfProjectedBand(
  points: ReadonlyArray<readonly [number, number]>,
  axisX: number,
  axisZ: number,
  minCoord: number,
  maxCoord: number,
): [number, number] | null {
  let sumX = 0;
  let sumZ = 0;
  let count = 0;
  for (const point of points) {
    const coord = point[0] * axisX + point[1] * axisZ;
    if (coord < minCoord || coord > maxCoord) continue;
    sumX += point[0];
    sumZ += point[1];
    count++;
  }
  return count > 0 ? [sumX / count, sumZ / count] : null;
}

function mergeDuplicateBridgeFootprints(
  bridges: BridgeFootprint[],
): BridgeFootprint[] {
  const merged: BridgeFootprint[] = [];
  for (const bridge of bridges) {
    const existing = merged.find((candidate) =>
      areDuplicateBridges(candidate, bridge),
    );
    if (!existing) {
      merged.push({ ...bridge });
      continue;
    }
    const existingPixels = existing.pixelCount;
    const totalPixels = existingPixels + bridge.pixelCount;
    existing.yaw = averageAxialYaw(
      existing.yaw,
      existingPixels,
      bridge.yaw,
      bridge.pixelCount,
    );
    existing.x =
      (existing.x * existingPixels + bridge.x * bridge.pixelCount) /
      totalPixels;
    existing.z =
      (existing.z * existingPixels + bridge.z * bridge.pixelCount) /
      totalPixels;
    existing.length = Math.max(existing.length, bridge.length);
    existing.roadWidth = Math.min(existing.roadWidth, bridge.roadWidth);
    existing.width =
      existing.roadWidth +
      SIDE_SHOULDER_WIDTH_M * 2 +
      SIDE_PARAPET_ALLOWANCE_M * 2;
    existing.deckBaseMinusY = Math.max(
      existing.deckBaseMinusY ?? existing.deckBaseY ?? FALLBACK_DECK_BASE_Y,
      bridge.deckBaseMinusY ?? bridge.deckBaseY ?? FALLBACK_DECK_BASE_Y,
    );
    existing.deckBasePlusY = Math.max(
      existing.deckBasePlusY ?? existing.deckBaseY ?? FALLBACK_DECK_BASE_Y,
      bridge.deckBasePlusY ?? bridge.deckBaseY ?? FALLBACK_DECK_BASE_Y,
    );
    existing.deckBaseY =
      (existing.deckBaseMinusY + existing.deckBasePlusY) * 0.5;
    existing.terrainMinusY = Math.max(
      existing.terrainMinusY ?? FALLBACK_DECK_BASE_Y,
      bridge.terrainMinusY ?? FALLBACK_DECK_BASE_Y,
    );
    existing.terrainPlusY = Math.max(
      existing.terrainPlusY ?? FALLBACK_DECK_BASE_Y,
      bridge.terrainPlusY ?? FALLBACK_DECK_BASE_Y,
    );
    existing.pixelCount = totalPixels;
  }
  return merged;
}

function averageAxialYaw(
  yawA: number,
  weightA: number,
  yawB: number,
  weightB: number,
): number {
  let axisBX = Math.cos(yawB);
  let axisBZ = Math.sin(yawB);
  const axisAX = Math.cos(yawA);
  const axisAZ = Math.sin(yawA);
  if (axisAX * axisBX + axisAZ * axisBZ < 0) {
    axisBX = -axisBX;
    axisBZ = -axisBZ;
  }
  const axisX = axisAX * weightA + axisBX * weightB;
  const axisZ = axisAZ * weightA + axisBZ * weightB;
  return Math.atan2(axisZ, axisX);
}

function areDuplicateBridges(a: BridgeFootprint, b: BridgeFootprint): boolean {
  const yawDelta = Math.abs(
    Math.atan2(Math.sin(a.yaw - b.yaw), Math.cos(a.yaw - b.yaw)),
  );
  if (
    Math.min(yawDelta, Math.abs(Math.PI - yawDelta)) >
    DUPLICATE_YAW_TOLERANCE_RAD
  ) {
    return false;
  }

  const axisX = Math.cos(a.yaw);
  const axisZ = Math.sin(a.yaw);
  const widthAxisX = -axisZ;
  const widthAxisZ = axisX;
  const deltaX = b.x - a.x;
  const deltaZ = b.z - a.z;
  const spanOffset = Math.abs(deltaX * axisX + deltaZ * axisZ);
  const lateralOffset = Math.abs(deltaX * widthAxisX + deltaZ * widthAxisZ);
  const maxSpanOffset = Math.max(
    DUPLICATE_CENTER_TOLERANCE_M,
    Math.min(a.length, b.length) * 0.45,
  );
  const maxLateralOffset = Math.max(
    DUPLICATE_LATERAL_TOLERANCE_M,
    (a.width + b.width) * 0.75,
  );
  return spanOffset <= maxSpanOffset && lateralOffset <= maxLateralOffset;
}

function bridgeBankLanding(
  availableRoadM: number | null,
  cellM: number,
): number {
  const desiredLanding = Math.max(BANK_LANDING_M, cellM * 0.35);
  if (availableRoadM === null) return desiredLanding;
  return Math.max(0, Math.min(desiredLanding, availableRoadM));
}

function bridgeDeckProfileFromHeightmap(
  terrain: TerrainAsset,
  spanAxisX: number,
  spanAxisZ: number,
  widthAxisX: number,
  widthAxisZ: number,
  spanMin: number,
  spanMax: number,
  widthMid: number,
  roadWidth: number,
): {
  deckBaseY: number;
  deckBaseMinusY: number;
  deckBasePlusY: number;
  terrainMinusY: number;
  terrainPlusY: number;
} {
  const spanInset = Math.min(
    BANK_LANDING_M * 0.5,
    Math.max(0, (spanMax - spanMin) * 0.15),
  );
  const minusTerrainY = bridgeRoadSurfaceHeightAtSpans(
    terrain,
    spanAxisX,
    spanAxisZ,
    widthAxisX,
    widthAxisZ,
    [spanMin, spanMin + spanInset],
    widthMid,
    roadWidth,
  );
  const plusTerrainY = bridgeRoadSurfaceHeightAtSpans(
    terrain,
    spanAxisX,
    spanAxisZ,
    widthAxisX,
    widthAxisZ,
    [spanMax - spanInset, spanMax],
    widthMid,
    roadWidth,
  );
  const terrainMinusY = Number.isFinite(minusTerrainY)
    ? minusTerrainY
    : FALLBACK_DECK_BASE_Y + DECK_THICKNESS_M + SURFACE_THICKNESS_M;
  const terrainPlusY = Number.isFinite(plusTerrainY)
    ? plusTerrainY
    : terrainMinusY;
  const deckBaseMinusY = roadSurfaceToDeckBase(terrainMinusY);
  const deckBasePlusY = roadSurfaceToDeckBase(terrainPlusY);

  return {
    deckBaseY: (deckBaseMinusY + deckBasePlusY) * 0.5,
    deckBaseMinusY,
    deckBasePlusY,
    terrainMinusY,
    terrainPlusY,
  };
}

function bridgeRoadSurfaceHeightAtSpans(
  terrain: TerrainAsset,
  spanAxisX: number,
  spanAxisZ: number,
  widthAxisX: number,
  widthAxisZ: number,
  spanSamples: readonly number[],
  widthMid: number,
  roadWidth: number,
): number {
  let targetRoadSurfaceY = -Infinity;
  const widthSamples = [
    widthMid - roadWidth * 0.35,
    widthMid,
    widthMid + roadWidth * 0.35,
  ];

  for (const spanCoord of spanSamples) {
    for (const widthCoord of widthSamples) {
      const sampleX = spanAxisX * spanCoord + widthAxisX * widthCoord;
      const sampleZ = spanAxisZ * spanCoord + widthAxisZ * widthCoord;
      targetRoadSurfaceY = Math.max(
        targetRoadSurfaceY,
        sampleTerrainHeight(terrain, sampleX, sampleZ),
      );
    }
  }

  return targetRoadSurfaceY;
}

function roadSurfaceToDeckBase(roadSurfaceY: number): number {
  return (
    roadSurfaceY +
    ROAD_SURFACE_TERRAIN_MARGIN_M -
    DECK_THICKNESS_M -
    SURFACE_THICKNESS_M
  );
}

function localAnnotationSurfacePointsNearComponent(
  terrain: TerrainAsset,
  maskWidth: number,
  maskHeight: number,
  component: Array<[number, number]>,
  surface: TerrainSurface,
  padPx?: number,
): Array<[number, number]> {
  const surfaceMask = buildTerrainSurfaceMask(terrain, surface);
  if (
    !surfaceMask ||
    surfaceMask.width !== maskWidth ||
    surfaceMask.height !== maskHeight
  ) {
    return [];
  }
  const bounds = expandedComponentBounds(
    component,
    maskWidth,
    maskHeight,
    padPx,
  );
  const worldWidth = terrain.worldWidthM;
  const worldHeight = terrain.worldHeightM;
  const halfWorldWidth = worldWidth * 0.5;
  const halfWorldHeight = worldHeight * 0.5;
  const pxToWorldX = worldWidth / maskWidth;
  const pxToWorldZ = worldHeight / maskHeight;
  const points: Array<[number, number]> = [];

  for (let row = bounds.minRow; row <= bounds.maxRow; row++) {
    for (let col = bounds.minCol; col <= bounds.maxCol; col++) {
      const idx = row * maskWidth + col;
      if (!surfaceMask.mask[idx]) continue;
      points.push([
        -halfWorldWidth + (col + 0.5) * pxToWorldX,
        -halfWorldHeight + (row + 0.5) * pxToWorldZ,
      ]);
    }
  }
  return points;
}

function expandedComponentBounds(
  component: Array<[number, number]>,
  width: number,
  height: number,
  padPx?: number,
): { minCol: number; maxCol: number; minRow: number; maxRow: number } {
  let minCol = width;
  let maxCol = -1;
  let minRow = height;
  let maxRow = -1;
  for (const [col, row] of component) {
    minCol = Math.min(minCol, col);
    maxCol = Math.max(maxCol, col);
    minRow = Math.min(minRow, row);
    maxRow = Math.max(maxRow, row);
  }
  const componentSize = Math.max(maxCol - minCol + 1, maxRow - minRow + 1);
  const pad = padPx ?? Math.max(8, componentSize * 3);
  return {
    minCol: Math.max(0, minCol - pad),
    maxCol: Math.min(width - 1, maxCol + pad),
    minRow: Math.max(0, minRow - pad),
    maxRow: Math.min(height - 1, maxRow + pad),
  };
}

function pcaAxis(points: ReadonlyArray<readonly [number, number]>): {
  centerX: number;
  centerZ: number;
  axisX: number;
  axisZ: number;
} | null {
  if (points.length < 2) return null;
  let centerX = 0;
  let centerZ = 0;
  for (const [pointX, pointZ] of points) {
    centerX += pointX;
    centerZ += pointZ;
  }
  centerX /= points.length;
  centerZ /= points.length;

  let covXX = 0;
  let covXZ = 0;
  let covZZ = 0;
  for (const [pointX, pointZ] of points) {
    const deltaX = pointX - centerX;
    const deltaZ = pointZ - centerZ;
    covXX += deltaX * deltaX;
    covXZ += deltaX * deltaZ;
    covZZ += deltaZ * deltaZ;
  }
  const inverseCount = 1 / points.length;
  covXX *= inverseCount;
  covXZ *= inverseCount;
  covZZ *= inverseCount;

  const trace = covXX + covZZ;
  const determinant = covXX * covZZ - covXZ * covXZ;
  const discriminant = Math.max(0, trace * trace * 0.25 - determinant);
  const eigenvalue = trace * 0.5 + Math.sqrt(discriminant);
  let axisX = covXZ;
  let axisZ = eigenvalue - covXX;
  if (Math.abs(axisX) < 1e-9 && Math.abs(axisZ) < 1e-9) {
    axisX = 1;
    axisZ = 0;
  }
  const axisLength = Math.hypot(axisX, axisZ);
  if (axisLength < 1e-6) return null;
  return {
    centerX,
    centerZ,
    axisX: axisX / axisLength,
    axisZ: axisZ / axisLength,
  };
}

function projectedExtents(
  points: ReadonlyArray<readonly [number, number]>,
  axisX: number,
  axisZ: number,
): { min: number; max: number } | null {
  if (points.length === 0) return null;
  let min = Infinity;
  let max = -Infinity;
  for (const [pointX, pointZ] of points) {
    const coord = pointX * axisX + pointZ * axisZ;
    min = Math.min(min, coord);
    max = Math.max(max, coord);
  }
  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : null;
}

export function createBridgeMesh(
  bridge: BridgeFootprint,
  label: string,
): MeshAsset {
  return mergeMeshes(
    bridgeParts(bridge).map((part) => ({
      mesh: createBox(part.sizeX, part.sizeY, part.sizeZ, part.color, part.id),
      transform: ([pointX, pointY, pointZ]) => [
        pointX + part.centerX,
        pointY +
          part.centerY +
          (part.slopeYPerX ?? 0) * (pointX + part.centerX),
        pointZ + part.centerZ,
      ],
    })),
    label,
  );
}

function bridgeParts(bridge: BridgeFootprint): BridgeBoxPart[] {
  const totalWidth = bridge.width;
  const roadWidth = bridge.roadWidth;
  const shoulderWidth = SIDE_SHOULDER_WIDTH_M;
  const deckBaseMinusY =
    bridge.deckBaseMinusY ?? bridge.deckBaseY ?? FALLBACK_DECK_BASE_Y;
  const deckBasePlusY =
    bridge.deckBasePlusY ?? bridge.deckBaseY ?? FALLBACK_DECK_BASE_Y;
  const deckBaseY = (deckBaseMinusY + deckBasePlusY) * 0.5;
  const deckSlopeYPerX =
    bridge.length > 1e-6 ? (deckBasePlusY - deckBaseMinusY) / bridge.length : 0;
  const terrainMinusY = bridge.terrainMinusY ?? deckBaseMinusY;
  const terrainPlusY = bridge.terrainPlusY ?? deckBasePlusY;
  const abutmentWidth = totalWidth + SIDE_OVERHANG_M * 2;
  const deckLength = bridge.length + END_BEARING_M * 2;
  const deckCenterY = deckBaseY + DECK_THICKNESS_M * 0.5;
  const surfaceCenterY =
    deckBaseY + DECK_THICKNESS_M + SURFACE_THICKNESS_M * 0.5;
  const abutmentOffsetX = Math.max(
    0,
    bridge.length * 0.5 - ABUTMENT_THICKNESS_M * 0.5,
  );
  const abutmentMinus = abutmentPart(
    "abutment_minus",
    -abutmentOffsetX,
    deckBaseMinusY,
    terrainMinusY,
    abutmentWidth,
  );
  const abutmentPlus = abutmentPart(
    "abutment_plus",
    abutmentOffsetX,
    deckBasePlusY,
    terrainPlusY,
    abutmentWidth,
  );

  const parts: BridgeBoxPart[] = [
    {
      id: "deck",
      centerX: 0,
      centerY: deckCenterY,
      centerZ: 0,
      sizeX: deckLength,
      sizeY: DECK_THICKNESS_M,
      sizeZ: totalWidth,
      color: CONCRETE_TOP,
      collides: true,
      slopeYPerX: deckSlopeYPerX,
    },
    {
      id: "road_surface",
      centerX: 0,
      centerY: surfaceCenterY,
      centerZ: 0,
      sizeX: deckLength,
      sizeY: SURFACE_THICKNESS_M,
      sizeZ: roadWidth,
      color: ROAD_TOP,
      collides: false,
      slopeYPerX: deckSlopeYPerX,
    },
    {
      id: "shoulder_port",
      centerX: 0,
      centerY: surfaceCenterY,
      centerZ: -(roadWidth * 0.5 + shoulderWidth * 0.5),
      sizeX: deckLength,
      sizeY: SURFACE_THICKNESS_M,
      sizeZ: shoulderWidth,
      color: SHOULDER_TOP,
      collides: false,
      slopeYPerX: deckSlopeYPerX,
    },
    {
      id: "shoulder_starboard",
      centerX: 0,
      centerY: surfaceCenterY,
      centerZ: roadWidth * 0.5 + shoulderWidth * 0.5,
      sizeX: deckLength,
      sizeY: SURFACE_THICKNESS_M,
      sizeZ: shoulderWidth,
      color: SHOULDER_TOP,
      collides: false,
      slopeYPerX: deckSlopeYPerX,
    },
    abutmentMinus,
    abutmentPlus,
  ];

  for (const sideSign of [-1, 1] as const) {
    parts.push({
      id: sideSign < 0 ? "parapet_port" : "parapet_starboard",
      centerX: 0,
      centerY: deckBaseY + DECK_THICKNESS_M + PARAPET_HEIGHT_M * 0.5,
      centerZ:
        sideSign * (roadWidth * 0.5 + shoulderWidth + PARAPET_WIDTH_M * 0.5),
      sizeX: deckLength,
      sizeY: PARAPET_HEIGHT_M,
      sizeZ: PARAPET_WIDTH_M,
      color: CONCRETE_TOP,
      collides: true,
      slopeYPerX: deckSlopeYPerX,
    });
  }

  return parts;
}

function abutmentPart(
  id: string,
  centerX: number,
  deckBaseY: number,
  terrainY: number,
  width: number,
): BridgeBoxPart {
  const wallBottom = Math.min(WALL_BOTTOM_M, terrainY - 0.08, deckBaseY - 0.1);
  const wallHeight = Math.max(0.1, deckBaseY - wallBottom);
  return {
    id,
    centerX,
    centerY: wallBottom + wallHeight * 0.5,
    centerZ: 0,
    sizeX: ABUTMENT_THICKNESS_M,
    sizeY: wallHeight,
    sizeZ: width,
    color: CONCRETE_FACE,
    collides: true,
  };
}

export function bridgeInstanceMatrix(
  bridge: BridgeFootprint,
  waterLevel: number,
): Mat4 {
  const cosYaw = Math.cos(bridge.yaw);
  const sinYaw = Math.sin(bridge.yaw);
  // prettier-ignore
  return new Float32Array([
    cosYaw,  0, sinYaw, 0,
    0,       1, 0,      0,
    -sinYaw, 0, cosYaw, 0,
    bridge.x, waterLevel, bridge.z, 1,
  ]) as Mat4;
}

export function bridgeAabbs(
  bridge: BridgeFootprint,
  waterLevel: number,
): Aabb[] {
  return bridgeParts(bridge)
    .filter((part) => part.collides)
    .map((part) => orientedPartAabb(bridge, waterLevel, part));
}

function orientedPartAabb(
  bridge: BridgeFootprint,
  waterLevel: number,
  part: BridgeBoxPart,
): Aabb {
  const cosYaw = Math.cos(bridge.yaw);
  const sinYaw = Math.sin(bridge.yaw);
  const halfX = part.sizeX * 0.5;
  const halfZ = part.sizeZ * 0.5;
  const halfY = part.sizeY * 0.5;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  for (const localX of [part.centerX - halfX, part.centerX + halfX]) {
    for (const localZ of [part.centerZ - halfZ, part.centerZ + halfZ]) {
      const worldX = bridge.x + cosYaw * localX - sinYaw * localZ;
      const worldZ = bridge.z + sinYaw * localX + cosYaw * localZ;
      minX = Math.min(minX, worldX);
      maxX = Math.max(maxX, worldX);
      minZ = Math.min(minZ, worldZ);
      maxZ = Math.max(maxZ, worldZ);
    }
    for (const localY of [part.centerY - halfY, part.centerY + halfY]) {
      const worldY = waterLevel + localY + (part.slopeYPerX ?? 0) * localX;
      minY = Math.min(minY, worldY);
      maxY = Math.max(maxY, worldY);
    }
  }

  return {
    min: [minX, minY, minZ],
    max: [maxX, maxY, maxZ],
    kind: "bridge",
  };
}
